import React from 'react';
import { Button } from 'react-bootstrap';
import { Calendar, Edit2, Folder, Home, Link as LinkIcon, LucideIcon, User } from 'lucide-react';
import { formatDateForDisplay } from '../../../../Helpers/DateUtils';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import './shared.css';

export interface InternalAssigneeRow {
    key: string;
    name: string;
}

export interface ExternalAssigneeRow {
    key: string;
    name: string;
    /** Guest link for this delegation; needed for "Kopiera länk" */
    link?: string;
}

export interface DetailsListProps {
    /** Storage format (YYYY-MM-DD hh:mm:ss) */
    createdAt: string;
    createdByName?: string;
    /** False when the page shows Skapad elsewhere (the admin modal, under the description) */
    showCreated?: boolean;
    /** YYYY-MM-DD or YYYY-MM-DD hh:mm:ss */
    dueDate?: string;
    /** Red due date */
    overdue?: boolean;
    onEditDueDate?: () => void;
    /** Shown in place of the due date while it is being edited */
    dueDateEditor?: React.ReactNode;
    /** Påminnelser, after Tilldelad (admin modal; only when external delegates exist, the only ones reminded) */
    reminders?: React.ReactNode;
    projectLabel?: string;
    onEditProject?: () => void;
    /** Shown in place of the project while it is being edited */
    projectEditor?: React.ReactNode;
    unitLabel?: string;
    isOpen: boolean;
    /** Why a closed work order is closed; picks the note under the external rows (default completed) */
    closedStatus?: 'completed' | 'cancelled';
    internalAssignees: InternalAssigneeRow[];
    externalAssignees: ExternalAssigneeRow[];
    onEditAssignment?: () => void;
    /** When given, each external row shows a "Kopiera länk" button (only while open) */
    onCopyLink?: (link: string) => void;
}

/** "Skapad 2026-10-01 av Anna", the line the admin modal shows under the description. */
export const CreatedLine: React.FC<{ createdAt: string; createdByName?: string }> = ({ createdAt, createdByName }) => (
    <>
        {formatDateForDisplay(createdAt)}
        {createdByName && <span className="text-muted"> {WORK_ORDER_LABELS.createdBy} {createdByName}</span>}
    </>
);

/** Bold label after a muted icon, with an optional Ändra / Lägg till link. */
export const DetailLabel: React.FC<{
    icon: LucideIcon;
    label: string;
    onEdit?: () => void;
    /** "Lägg till" instead of "Ändra" when there is no value yet */
    empty?: boolean;
    /** Render as a section heading (h3) rather than a field label */
    heading?: boolean;
}> = ({ icon: Icon, label, onEdit, empty, heading }) => {
    const Tag = heading ? 'h3' : 'div';
    return (
    <Tag className="wo-section-title">
        <Icon size={16} className="wo-section-title__icon" aria-hidden="true" />
        <span>{label}</span>
        {onEdit && (
            <Button
                variant="link"
                className="wo-edit-link"
                onClick={onEdit}
                aria-label={`${empty ? WORK_ORDER_LABELS.add : WORK_ORDER_LABELS.edit} ${label.toLowerCase()}`}
            >
                <Edit2 size={12} aria-hidden="true" />
                {empty ? WORK_ORDER_LABELS.add : WORK_ORDER_LABELS.edit}
            </Button>
        )}
    </Tag>
    );
};

/**
 * Detaljer: Projekt, Förfallodatum and Tilldelad as equal columns (then
 * Påminnelser, Lägenhet/lokal and Skapad when shown), each a bold label with an icon and
 * its own edit link. Editors open in place of the value.
 */
export const DetailsList: React.FC<DetailsListProps> = ({
    createdAt,
    createdByName,
    showCreated = true,
    dueDate,
    overdue,
    onEditDueDate,
    dueDateEditor,
    reminders,
    projectLabel,
    onEditProject,
    projectEditor,
    unitLabel,
    isOpen,
    internalAssignees,
    externalAssignees,
    onEditAssignment,
    onCopyLink,
    closedStatus = 'completed'
}) => {
    const hasAssignees = internalAssignees.length > 0 || externalAssignees.length > 0;
    const linksExpired = !isOpen && externalAssignees.length > 0;
    const showCopy = Boolean(onCopyLink) && !linksExpired;

    return (
        <div className="wo-details wo-card" data-testid={workOrderSectionTestId('details')}>
            <div className="wo-details__item">
                <DetailLabel
                    icon={Folder}
                    label={WORK_ORDER_LABELS.project}
                    onEdit={projectEditor ? undefined : onEditProject}
                    empty={!projectLabel}
                />
                {projectEditor ?? <div>{projectLabel ?? '–'}</div>}
            </div>

            <div className="wo-details__item">
                <DetailLabel
                    icon={Calendar}
                    label={WORK_ORDER_LABELS.dueDate}
                    onEdit={dueDateEditor ? undefined : onEditDueDate}
                    empty={!dueDate}
                />
                {dueDateEditor ?? (
                    <div className={overdue ? 'text-danger' : undefined}>
                        {dueDate ? formatDateForDisplay(dueDate) : '–'}
                    </div>
                )}
            </div>


            <div className="wo-details__item">
                <DetailLabel
                    icon={User}
                    label={WORK_ORDER_LABELS.assigned}
                    onEdit={onEditAssignment}
                    empty={!hasAssignees}
                />
                <div className="wo-details__assignees">
                    {!hasAssignees && <span className="text-muted">{WORK_ORDER_LABELS.unassigned}</span>}
                    {internalAssignees.map((assignee) => (
                        <span key={assignee.key} className="wo-details__assignee">{assignee.name}</span>
                    ))}
                    {externalAssignees.map((assignee) => (
                        <span key={assignee.key} className="wo-details__assignee">
                            {assignee.name}
                            <span className="wo-details__external">{WORK_ORDER_LABELS.external}</span>
                            {showCopy && assignee.link && onCopyLink && (
                                <Button
                                    variant="link"
                                    className="wo-edit-link"
                                    onClick={() => onCopyLink(assignee.link as string)}
                                >
                                    <LinkIcon size={12} aria-hidden="true" />
                                    {WORK_ORDER_LABELS.copyLink}
                                </Button>
                            )}
                        </span>
                    ))}
                </div>
                {linksExpired && (
                    <small className="text-muted d-block mt-1">
                        {closedStatus === 'cancelled' ? WORK_ORDER_LABELS.externalLinksCancelled : WORK_ORDER_LABELS.externalCompletedReadOnly}
                    </small>
                )}
            </div>

            {reminders && <div className="wo-details__item">{reminders}</div>}

            {unitLabel && (
                <div className="wo-details__item">
                    <DetailLabel icon={Home} label={WORK_ORDER_LABELS.unit} />
                    <div>{unitLabel}</div>
                </div>
            )}

            {showCreated && (
                <div className="wo-details__item">
                    <DetailLabel icon={Calendar} label={WORK_ORDER_LABELS.created} />
                    <div><CreatedLine createdAt={createdAt} createdByName={createdByName} /></div>
                </div>
            )}
        </div>
    );
};

export default DetailsList;
